<template>
  <div class="stickers-tab">
    <el-dialog
      :visible.sync="uploadDialogVisible"
      title="Upload Sticker"
      width="420px"
      @close="clearSelectedSticker"
    >
      <el-upload
        action=""
        accept=".webp,.png,.jpg,.jpeg,image/webp,image/png,image/jpeg"
        :auto-upload="false"
        :show-file-list="false"
        :on-change="handleFileChange"
        ref="fileUpload"
        class="sticker-upload-button"
      >
        <div class="selected-sticker-modal-preview">
          <img
            v-if="selectedPreviewUrl"
            :src="selectedPreviewUrl"
            :alt="stickerName"
            class="selected-sticker-modal-image"
          />
          <div v-else class="selected-sticker-modal-placeholder">
            <i class="el-icon-plus"></i>
            <span>Select Image</span>
          </div>
        </div>
      </el-upload>

      <el-input
        v-model="stickerName"
        size="small"
        v-if="selectedFile"
        class="sticker-name-field"
        placeholder="Sticker name"
        :disabled="!selectedFile"
      />

      <div v-if="selectedFile" class="selected-sticker-metadata">
        <div>
          <span>File</span>
          <strong>{{ selectedFile.name }}</strong>
        </div>
        <div>
          <span>Size</span>
          <strong>{{ formatSize(selectedFile.size) }}</strong>
        </div>
        <div>
          <span>Type</span>
          <strong>{{ selectedFile.type }}</strong>
        </div>
      </div>

      <span slot="footer">
        <el-button size="small" @click="uploadDialogVisible = false">Cancel</el-button>
        <el-button
          size="small"
          type="primary"
          :disabled="!selectedFile"
          :loading="isUploading"
          @click="uploadSticker"
        >
          Save
        </el-button>
      </span>
    </el-dialog>

    <el-alert
      v-if="!cloudAPIEnabled"
      type="warning"
      :closable="false"
      title="Sticker library is only available for WhatsApp Cloud API."
    />

    <div class="sticker-toolbar">
      <el-input
        v-model="whatsappStickerSearch"
        clearable
        size="small"
        placeholder="Search stickers"
        prefix-icon="el-icon-search"
        @input="resetWhatsappStickerSearch"
        @clear="resetWhatsappStickerSearch"
      />
    </div>

    <div v-loading="isLoadingWhatsappStickers" class="sticker-grid">
      <button class="sticker-add-card" type="button" @click="openUploadDialog">
        <i class="el-icon-plus"></i>
      </button>

      <div
        v-for="sticker in whatsappStickers"
        :key="sticker.id"
        class="sticker-card"
        @click="openStickerDetails(sticker)"
      >
        <div class="sticker-preview">
          <img
            v-if="whatsappStickerPreviewUrls[sticker.id]"
            :src="whatsappStickerPreviewUrls[sticker.id]"
            :alt="sticker.name"
            class="sticker-image"
          />
          <i v-else class="el-icon-picture-outline sticker-placeholder"></i>
          <div class="sticker-card-actions">
            <el-switch
              :value="sticker.status === 'active'"
              active-color="#13ce66"
              inactive-color="#c0c4cc"
              @click.native.stop
              @change="toggleSticker(sticker, $event)"
            />
            <el-button
              size="mini"
              type="danger"
              icon="el-icon-delete"
              circle
              @click.stop="deleteSticker(sticker)"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="cloudAPIEnabled && !isLoadingWhatsappStickers && whatsappStickers.length === 0"
      class="sticker-empty"
    >
      No stickers found
    </div>

    <div v-if="whatsappStickerHasMore" class="sticker-pagination">
      <el-button
        size="small"
        :loading="isLoadingWhatsappStickers"
        @click="fetchMoreWhatsappStickers"
      >
        Load more
      </el-button>
    </div>

    <el-dialog
      :visible.sync="stickerDetailsDialogVisible"
      title="Sticker Details"
      width="420px"
      @close="clearSelectedLibrarySticker"
    >
      <div v-if="selectedLibrarySticker" class="library-sticker-dialog-content">
        <div class="library-sticker-preview">
          <img
            v-if="whatsappStickerPreviewUrls[selectedLibrarySticker.id]"
            :src="whatsappStickerPreviewUrls[selectedLibrarySticker.id]"
            :alt="selectedLibrarySticker.name"
            class="library-sticker-image"
          />
          <i v-else class="el-icon-picture-outline sticker-placeholder"></i>
        </div>

        <el-input
          v-model="selectedLibrarySticker.name"
          size="small"
          class="sticker-name-field"
          placeholder="Sticker name"
        />

        <div class="selected-sticker-metadata">
          <div>
            <span>File</span>
            <strong>{{ selectedLibrarySticker.file_name }}</strong>
          </div>
          <div>
            <span>Size</span>
            <strong>{{ formatSize(selectedLibrarySticker.file_size) }}</strong>
          </div>
          <div>
            <span>Type</span>
            <strong>{{ selectedLibrarySticker.mime_type }}</strong>
          </div>
          <div>
            <span>Animation</span>
            <strong>{{ selectedLibrarySticker.animated ? "Animated" : "Static" }}</strong>
          </div>
        </div>
      </div>

      <span slot="footer">
        <el-button size="small" @click="stickerDetailsDialogVisible = false">Cancel</el-button>
        <el-button
          size="small"
          type="primary"
          :disabled="!selectedLibrarySticker || !selectedLibrarySticker.name.trim()"
          :loading="isSavingSticker"
          @click="saveSelectedLibrarySticker"
        >
          Save
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Vue from "vue";

const ALLOWED_STICKER_MIME_TYPES = ["image/webp", "image/png", "image/jpeg"];
const DEFAULT_STICKER_LIMIT = 24;

export default Vue.extend({
  name: "StickersTab",
  props: {
    wa_endpoint: {
      type: String,
      required: true,
    },
    wa_id: {
      type: String,
      required: true,
    },
    cloudAPISettings: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoadingWhatsappStickers: false,
      isSavingSticker: false,
      isUploading: false,
      selectedLibrarySticker: null,
      selectedFile: null,
      selectedPreviewUrl: "",
      stickerDetailsDialogVisible: false,
      stickerName: "",
      uploadDialogVisible: false,
      whatsappStickerHasMore: false,
      whatsappStickerLimit: DEFAULT_STICKER_LIMIT,
      whatsappStickerOffset: 0,
      whatsappStickerPreviewUrls: {},
      whatsappStickerSearch: "",
      whatsappStickerTotal: 0,
      whatsappStickers: [],
    };
  },
  computed: {
    cloudAPIEnabled() {
      return this.cloudAPISettings?.enabled;
    },
  },
  watch: {
    wa_id: {
      immediate: true,
      handler() {
        this.fetchWhatsappStickers({ reset: true });
      },
    },
  },
  beforeDestroy() {
    this.clearSelectedStickerPreview();
  },
  methods: {
    arrayBufferToBase64(bytes) {
      const arr = Object.keys(bytes).map((key) => bytes[key]);
      const bin = arr.reduce((acc, curr) => `${acc}${String.fromCharCode(curr)}`, "");
      return btoa(bin);
    },
    formatSize(size) {
      if (!size) {
        return "0 KB";
      }
      return `${Math.ceil(size / 1024)} KB`;
    },
    openUploadDialog() {
      this.clearSelectedSticker();
      this.uploadDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.fileUpload?.$el?.querySelector("input[type='file']")?.click();
      });
    },
    handleFileChange(file) {
      if (!ALLOWED_STICKER_MIME_TYPES.includes(file.raw?.type)) {
        this.$message.error("Sticker file must be image/webp, image/png, or image/jpeg.");
        this.clearSelectedSticker();
        return;
      }
      this.clearSelectedStickerPreview();
      this.selectedFile = file.raw;
      this.stickerName = file.name;
      this.selectedPreviewUrl = URL.createObjectURL(file.raw);
    },
    clearSelectedStickerPreview() {
      if (this.selectedPreviewUrl) {
        URL.revokeObjectURL(this.selectedPreviewUrl);
        this.selectedPreviewUrl = "";
      }
    },
    clearSelectedSticker() {
      this.clearSelectedStickerPreview();
      this.selectedFile = null;
      this.stickerName = "";
    },
    openStickerDetails(sticker) {
      this.selectedLibrarySticker = { ...sticker };
      this.stickerDetailsDialogVisible = true;
    },
    clearSelectedLibrarySticker() {
      this.selectedLibrarySticker = null;
    },
    fetchWhatsappStickers({ reset = false } = {}) {
      if (!this.cloudAPIEnabled || !this.wa_id) {
        this.whatsappStickers = [];
        this.whatsappStickerTotal = 0;
        this.whatsappStickerOffset = 0;
        this.whatsappStickerHasMore = false;
        return Promise.resolve({ stickers: [] });
      }

      const offset = reset ? 0 : this.whatsappStickerOffset;

      this.isLoadingWhatsappStickers = true;
      return this.$store
        .dispatch("WHATSAPP_GET_STICKERS", {
          data: {
            wa_id: this.wa_id,
            search: this.whatsappStickerSearch,
            limit: this.whatsappStickerLimit,
            offset,
          },
        })
        .then((result) => {
          const stickers = result.stickers || [];
          this.whatsappStickers = reset ? stickers : [...this.whatsappStickers, ...stickers];
          this.whatsappStickerTotal = result.total || this.whatsappStickers.length;
          this.whatsappStickerOffset = offset + stickers.length;
          this.whatsappStickerHasMore = Boolean(result.hasMore);
          stickers.forEach((sticker) => this.fetchWhatsappStickerPreview(sticker));
          return result;
        })
        .catch((error) => {
          this.$message.error("Failed to fetch WhatsApp stickers.");
          throw error;
        })
        .finally(() => {
          this.isLoadingWhatsappStickers = false;
        });
    },
    fetchMoreWhatsappStickers() {
      if (this.isLoadingWhatsappStickers || !this.whatsappStickerHasMore) {
        return Promise.resolve();
      }

      return this.fetchWhatsappStickers({ reset: false });
    },
    resetWhatsappStickerSearch() {
      this.whatsappStickerOffset = 0;
      this.whatsappStickerHasMore = false;
      return this.fetchWhatsappStickers({ reset: true });
    },
    fetchWhatsappStickerPreview(sticker) {
      if (!sticker.media_id || this.whatsappStickerPreviewUrls[sticker.id]) {
        return;
      }

      this.$store
        .dispatch("WHATSAPP_CLOUD_GET_MEDIA_BY_ID", {
          data: {
            endpoint: this.wa_endpoint,
            id: sticker.media_id,
          },
        })
        .then((res) => {
          const base64 = `data:${res.contentType};base64,${this.arrayBufferToBase64(res.data)}`;
          this.$set(this.whatsappStickerPreviewUrls, sticker.id, base64);
        })
        .catch(() => {
          this.$set(this.whatsappStickerPreviewUrls, sticker.id, "");
        });
    },
    uploadSticker() {
      if (!this.selectedFile || !this.wa_id) {
        return;
      }

      this.isUploading = true;
      this.$store
        .dispatch("WHATSAPP_UPLOAD_STICKER", {
          file: this.selectedFile,
          name: this.stickerName,
          wa_id: this.wa_id,
        })
        .then((sticker) => {
          this.$message.success("Sticker uploaded.");
          this.uploadDialogVisible = false;
          this.clearSelectedSticker();
          this.fetchWhatsappStickerPreview(sticker);
          this.fetchWhatsappStickers({ reset: true });
        })
        .catch((err) => {
          const message = err?.response?.data?.error?.message || "Failed to upload sticker.";
          this.$message.error(message);
        })
        .finally(() => {
          this.isUploading = false;
        });
    },
    saveSelectedLibrarySticker() {
      if (!this.selectedLibrarySticker) {
        return;
      }

      this.isSavingSticker = true;
      return this.$store
        .dispatch("WHATSAPP_UPDATE_STICKER", {
          wa_id: this.wa_id,
          data: {
            id: this.selectedLibrarySticker.id,
            name: this.selectedLibrarySticker.name.trim(),
            description: this.selectedLibrarySticker.description,
            status: this.selectedLibrarySticker.status,
          },
        })
        .then(() => {
          this.$message.success("Sticker updated.");
          this.stickerDetailsDialogVisible = false;
          this.clearSelectedLibrarySticker();
          return this.fetchWhatsappStickers({ reset: true });
        })
        .catch((err) => {
          const message = err?.response?.data?.error?.message || "Failed to update sticker.";
          this.$message.error(message);
        })
        .finally(() => {
          this.isSavingSticker = false;
        });
    },
    toggleSticker(sticker, enabled) {
      return this.$store
        .dispatch("WHATSAPP_UPDATE_STICKER", {
          wa_id: this.wa_id,
          data: {
            id: sticker.id,
            name: sticker.name,
            description: sticker.description,
            status: enabled ? "active" : "inactive",
          },
        })
        .then(() => this.fetchWhatsappStickers({ reset: true }));
    },
    deleteSticker(sticker) {
      this.$confirm("Delete this sticker from the library?", "Delete Sticker", {
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        type: "warning",
      }).then(() => {
        return this.$store
          .dispatch("WHATSAPP_DELETE_STICKER", {
            wa_id: this.wa_id,
            data: { id: sticker.id },
          })
          .then(() => this.fetchWhatsappStickers({ reset: true }));
      });
    },
  },
});
</script>

<style scoped>
.stickers-tab {
  padding-top: 8px;
}

.sticker-toolbar {
  width: 280px;
  margin-bottom: 12px;
}

.sticker-upload-button ::v-deep .el-upload {
  display: block;
  margin-bottom: 0;
}

.sticker-upload-button {
  display: block;
  margin-bottom: 16px;
}

.selected-sticker-modal-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 180px;
  height: 180px;
  margin: 0 auto;
  border: 1px dashed #c0c4cc;
  border-radius: 4px;
  background: #f5f7fa;
  cursor: pointer;
}

.selected-sticker-modal-preview:hover {
  border-color: #409eff;
}

.selected-sticker-modal-image {
  width: 180px;
  height: 180px;
  object-fit: contain;
}

.selected-sticker-modal-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #909399;
  font-size: 13px;
}

.selected-sticker-modal-placeholder i {
  font-size: 28px;
}

.sticker-name-field {
  width: 100%;
}

.selected-sticker-metadata {
  display: grid;
  gap: 8px;
  margin-top: 14px;
  color: #606266;
  font-size: 13px;
}

.selected-sticker-metadata div {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.selected-sticker-metadata strong {
  min-width: 0;
  overflow: hidden;
  color: #303133;
  font-weight: 500;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.library-sticker-dialog-content {
  display: flex;
  flex-direction: column;
}

.library-sticker-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 180px;
  height: 180px;
  margin: 0 auto 16px;
  background: #f5f7fa;
}

.library-sticker-image {
  width: 180px;
  height: 180px;
  object-fit: contain;
}

.sticker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 180px);
  gap: 12px;
}

.sticker-add-card {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 180px;
  height: 180px;
  padding: 12px;
  border: 1px dashed #c0c4cc;
  border-radius: 4px;
  background: #f5f7fa;
  color: #909399;
  cursor: pointer;
  font-size: 28px;
}

.sticker-add-card:hover {
  border-color: #409eff;
  color: #409eff;
}

.sticker-card {
  position: relative;
  display: flex;
  width: 180px;
  height: 180px;
  overflow: hidden;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.sticker-preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
}

.sticker-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sticker-placeholder {
  font-size: 32px;
  color: #909399;
}

.sticker-card-actions {
  position: absolute;
  right: 6px;
  bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.sticker-card:hover .sticker-card-actions {
  opacity: 1;
}

.sticker-card-actions ::v-deep .el-switch {
  transform: scale(0.82);
  transform-origin: left center;
}

.sticker-card-actions ::v-deep .el-button--mini.is-circle {
  padding: 6px;
}

.sticker-empty {
  margin-top: 16px;
  color: #909399;
  font-size: 13px;
}

.sticker-pagination {
  margin-top: 16px;
}
</style>
